import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { MessageSquare, ExternalLink, Star, Check, Clock } from "lucide-react"
import api from "../../services/api"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { Switch } from "../ui/switch"
import { Label } from "../ui/label"
import { Skeleton } from "../ui/skeleton"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card"
import { Avatar, AvatarFallback } from "../ui/avatar"

// Render star rating
function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-3.5 w-3.5 ${
            star <= rating
              ? "text-yellow-500 fill-yellow-500"
              : "text-muted-foreground"
          }`}
        />
      ))}
    </div>
  )
}

// Get initials from nickname
function getInitials(name) {
  if (!name) return "?"
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

// Format relative date
function formatRelativeDate(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return date.toLocaleDateString()
}

export default function ReviewsTable() {
  const [showUnrepliedOnly, setShowUnrepliedOnly] = useState(true)

  const { data, isLoading, error } = useQuery({
    queryKey: ["appstore-reviews", showUnrepliedOnly],
    queryFn: () => api.getAppStoreReviews(showUnrepliedOnly),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const reviews = data?.reviews || []

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle>Customer Reviews</CardTitle>
              <CardDescription>
                {showUnrepliedOnly 
                  ? `${data?.unrepliedCount || 0} reviews need a response`
                  : `${data?.totalCount || 0} total reviews`
                }
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="unreplied-filter"
              checked={showUnrepliedOnly}
              onCheckedChange={setShowUnrepliedOnly}
            />
            <Label htmlFor="unreplied-filter" className="text-sm cursor-pointer">
              Needs response only
            </Label>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            <p>Failed to load reviews</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="h-[200px] flex flex-col items-center justify-center text-muted-foreground">
            <Check className="h-12 w-12 mb-2 text-green-500" />
            <p className="font-medium">All caught up!</p>
            <p className="text-sm">No reviews need a response</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Rating</TableHead>
                  <TableHead>Review</TableHead>
                  <TableHead className="w-[100px]">Date</TableHead>
                  <TableHead className="w-[100px]">Territory</TableHead>
                  <TableHead className="w-[100px] text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell>
                      <StarRating rating={review.rating} />
                    </TableCell>
                    <TableCell>
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <div className="cursor-pointer">
                            <div className="flex items-center gap-2 mb-1">
                              <Avatar className="h-6 w-6">
                                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                                  {getInitials(review.reviewerNickname)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm font-medium">
                                {review.reviewerNickname || "Anonymous"}
                              </span>
                            </div>
                            <p className="text-sm font-medium line-clamp-1">
                              {review.title || "No title"}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {review.body}
                            </p>
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80" align="start">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-sm bg-primary/10 text-primary">
                                  {getInitials(review.reviewerNickname)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">
                                  {review.reviewerNickname || "Anonymous"}
                                </p>
                                <StarRating rating={review.rating} />
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-semibold">{review.title}</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {review.body}
                              </p>
                            </div>
                            {review.hasResponse && review.response && (
                              <div className="border-t pt-2 mt-2">
                                <p className="text-xs font-medium text-muted-foreground mb-1">
                                  Your Response:
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {review.response}
                                </p>
                              </div>
                            )}
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatRelativeDate(review.createdDate)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {review.territory || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {review.hasResponse ? (
                        <Badge variant="secondary" className="gap-1">
                          <Check className="h-3 w-3" />
                          Replied
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-600">
                          <Clock className="h-3 w-3" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        
        {/* Link to App Store Connect */}
        <div className="mt-4 pt-4 border-t flex justify-end">
          <Button variant="outline" size="sm" asChild>
            <a
              href="https://appstoreconnect.apple.com"
              target="_blank"
              rel="noopener noreferrer"
              className="gap-2"
            >
              Respond in App Store Connect
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
